import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useTheme } from '@mui/material/styles';
import { Typography } from '../../basic/typography/typography';
import RecentChatContainer from '@/components/layout/sidebar/recentChatContainer';
import { Icon } from '../../basic';
import { ThemedTooltip } from '@/components/chat/FeedbackContainer';

export interface SubSection {
  icon?: any;
  name: string;
  path: string;
  accessKey: string;
  urlLink?: string;
}
export interface Section {
  name: string;
  icon: any;
  heading?: boolean;
  subSections: SubSection[];
}
export interface ButtonStyles {
  padding?: string;
  color?: string;
  borderRadius?: number;
  backgroundColor?: string;
}

export const NavSection = ({
  section,
  accessiblePages,
  handleDrawerClick,
  customButtonStyles,
  setMobileOpen,
  closed,
  openedMenu,
}: {
  section: Section;
  accessiblePages: any[];
  handleDrawerClick: (x: string) => void;
  customButtonStyles?: ButtonStyles;
  setMobileOpen: (open: boolean) => void;
  closed: boolean;
  openedMenu: boolean;
}) => {
  // const [expanded, setExpanded] = useState(closed === true ? false : true);
  const location = useLocation();
  const { t } = useTranslation(['navMenu']);
  const theme = useTheme();
  // const { setClosed } = useContext(UserContext);
  const isClosed = closed;

  const accessibleSubSections = section.subSections.filter((sub: SubSection) =>
    accessiblePages.includes(sub.accessKey),
  );
  if (accessibleSubSections.length === 0) return <></>;

  const buttonStyle = {
    padding: customButtonStyles?.padding || '10px 10px',
    borderRadius: customButtonStyles?.borderRadius || '10px',
  };
  // this is for performance and explore sub list
  const renderSubSections = (subs: SubSection[]) =>
    subs.length ? (
      <List component="div" disablePadding data-testid="sub-sections">
        {subs.map((sub) => {
          const handleClick = () => {
            if (sub.urlLink) {
              // Open URL in new tab without preventing navigation
              window.open(sub.urlLink, '_blank', 'noopener,noreferrer');
            }
            handleDrawerClick(sub.name);
          };

          return (
            <ThemedTooltip
              key={sub.name}
              title={t(sub.name)}
              placement="right"
              data-testid="section-tooltip"
            >
              <ListItem
                disablePadding
                component={Link}
                to={sub.path}
                onClick={handleClick}
                data-testid={`sub-section-${sub.name}`}
              >
                <ListItemButton
                  sx={{
                    ...buttonStyle,
                    ...(isClosed === false && {
                      marginX: '10px',
                      paddingY: '6px',
                      marginBottom: '3px',
                    }),
                  }}
                  aria-haspopup="menu"
                  aria-expanded={openedMenu && closed}
                  selected={sub.path === location.pathname}
                  data-testid={`sub-section-button-${sub.name}`}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>{sub.icon}</ListItemIcon>
                  {!isClosed && (
                    <Typography
                      variant="bodySmall"
                      sx={{
                        fontWeight: '400',
                        fontSize: '14px',
                        letterSpacing: '0.4px',
                        color: theme.palette.primary.main,
                      }}
                    >
                      {t(sub.name)}
                    </Typography>
                  )}
                </ListItemButton>
              </ListItem>
            </ThemedTooltip>
          );
        })}
      </List>
    ) : null;

  const renderHeader = () => {
    const firstSub = accessibleSubSections[0];
    // const isSingle = section.subSections.length === 1;
    const Wrapper: React.ElementType = isClosed ? ListItemButton : Box;
    const isNewChat =
      section.subSections[0].accessKey === 'NEW_CHAT' && !isClosed;
    const newChatStyles = isNewChat
      ? {
          width: 'fit-content',
          height: '41px',
          border: '1px solid #B1B1B2',
          borderRadius: '10px',
          marginLeft: '8px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      : {
          width: 'min-content',
          border: '1px solid #B1B1B2',
          borderRadius: '10px',
          padding: '8px 8px',
        };

    return section.heading ? (
      <>
        <ThemedTooltip
          title={t(firstSub.name)}
          placement="right"
          disableHoverListener={!isClosed}
          data-testid="section-tooltip"
        >
          <Wrapper
            sx={{
              ...buttonStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            // onClick={() => {
            //   setExpanded(!expanded);
            //   setClosed(false);
            // }}
            data-testid="section-header"
          >
            {!isClosed ? (
              <Typography
                variant="bodyMedium"
                sx={{ fontSize: '14px', fontWeight: 'medium' }}
              >
                {t(firstSub.name)}
              </Typography>
            ) : (
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Icon icon={section.icon} sx={{ p: '2px' }} size="medium" />
              </ListItemIcon>
            )}
            {/* {!isSingle &&
              !closed &&
              (expanded ? <ExpandLess /> : <ExpandMore />)} */}
          </Wrapper>
        </ThemedTooltip>
        {!isClosed && firstSub.accessKey === 'HISTORY' && (
          <Box data-testid="recent-chat-container">
            <RecentChatContainer setMobileOpen={setMobileOpen} />
          </Box>
        )}
      </>
    ) : (
      <ThemedTooltip
        title={t(firstSub.name)}
        placement="right"
        key={!isClosed ? 'tooltip' : 'tooltip-closed'}
      >
        <Box>
          <ListItemButton
            component={Link}
            onClick={() => handleDrawerClick(firstSub.name)}
            sx={{ ...buttonStyle, ...newChatStyles }}
            to={firstSub.path}
            selected={
              firstSub.accessKey !== 'NEW_CHAT' &&
              firstSub.path === location.pathname
            }
            aria-haspopup="menu"
            aria-expanded={openedMenu && closed}
          >
            <Box display="flex" alignItems="center" justifyContent="center">
              <ListItemIcon
                sx={{
                  minWidth: isClosed ? 'auto' : 28,
                  color: isNewChat ? '#B1B1B2' : '',
                }}
              >
                <Icon icon={section.icon} sx={{ p: '2px' }} size="medium" />
              </ListItemIcon>
              {!isClosed && (
                <Typography
                  variant="bodyMedium"
                  sx={{ fontSize: '14px', fontWeight: 'medium' }}
                >
                  {t(firstSub.name)}
                </Typography>
              )}
            </Box>
          </ListItemButton>
        </Box>
      </ThemedTooltip>
    );
  };
  return (
    <Box
      key={section.name}
      position="relative"
      mb={1}
      data-testid="nav-section"
    >
      {renderHeader()}
      {/* <Collapse in={expanded} timeout="auto" unmountOnExit> */}
      {!isClosed && renderSubSections(accessibleSubSections.slice(1))}
      {/* </Collapse> */}
    </Box>
  );
};
